import { z } from 'zod';

/* Automations: "When <trigger> (and <conditions>), do <actions>." Written for non-technical admins. */

export const AUTOMATION_TRIGGERS = ['lead_created', 'status_changed', 'no_activity', 'followup_missed'] as const;
export type AutomationTrigger = (typeof AUTOMATION_TRIGGERS)[number];
export const TRIGGER_LABELS: Record<AutomationTrigger, string> = {
  lead_created: 'A new lead comes in',
  status_changed: 'A lead’s status changes',
  no_activity: 'Nobody has worked on a lead for some days',
  followup_missed: 'A follow-up is missed',
};

export const ACTION_TYPES = ['assign', 'add_tag', 'set_status', 'set_followup', 'notify', 'whatsapp_template', 'enroll_sequence'] as const;
export type ActionType = (typeof ACTION_TYPES)[number];
export const ACTION_LABELS: Record<ActionType, string> = {
  assign: 'Assign the lead',
  add_tag: 'Add a tag',
  set_status: 'Change the status',
  set_followup: 'Set a follow-up',
  notify: 'Send a notification',
  whatsapp_template: 'Send a WhatsApp template',
  enroll_sequence: 'Start a follow-up sequence',
};

/** Words that are replaced with the lead's details in messages: {{name}}, {{company}}, {{city}}, {{owner}}. */
export const MESSAGE_TOKENS = ['name', 'company', 'city', 'owner', 'phone'] as const;

const uuid = z.uuid({ error: 'Pick an option.' });
export const automationActionSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('assign'), userId: uuid.nullable().default(null) }), // null = use auto-assign rules
  z.object({ type: z.literal('add_tag'), tagId: uuid }),
  z.object({ type: z.literal('set_status'), statusId: uuid }),
  z.object({ type: z.literal('set_followup'), inHours: z.coerce.number().min(0).max(24 * 60), note: z.string().trim().max(200).default('') }),
  z.object({
    type: z.literal('notify'),
    to: z.union([z.enum(['owner', 'managers']), uuid]),
    message: z.string().trim().min(1, { error: 'Write the message.' }).max(300),
  }),
  z.object({
    type: z.literal('whatsapp_template'),
    templateName: z.string().trim().min(1, { error: 'Pick a template.' }).max(512),
    language: z.string().trim().min(2).max(15),
    params: z.array(z.string().trim().max(200)).max(10).default([]),
  }),
  z.object({ type: z.literal('enroll_sequence'), sequenceId: uuid }),
]);
export type AutomationAction = z.infer<typeof automationActionSchema>;

export const automationConditionsSchema = z.object({
  sourceIds: z.array(z.uuid()).max(50).default([]),
  campaignIds: z.array(z.uuid()).max(50).default([]),
  statusIds: z.array(z.uuid()).max(50).default([]),
  /** status_changed only: the new status. */
  toStatusIds: z.array(z.uuid()).max(50).default([]),
  /** no_activity: days without any activity. */
  days: z.coerce.number().int().min(1).max(365).optional(),
  /** followup_missed: hours after the follow-up time. */
  hours: z.coerce.number().int().min(1).max(24 * 30).optional(),
});

export const automationSchema = z
  .object({
    name: z.string().trim().min(1, { error: 'Give the rule a name.' }).max(100),
    isActive: z.boolean().default(true),
    trigger: z.enum(AUTOMATION_TRIGGERS),
    conditions: automationConditionsSchema.default({ sourceIds: [], campaignIds: [], statusIds: [], toStatusIds: [] }),
    actions: z.array(automationActionSchema).min(1, { error: 'Add at least one action.' }).max(10),
  })
  .refine((a) => a.trigger !== 'no_activity' || !!a.conditions.days, { error: 'How many days without activity?', path: ['conditions', 'days'] })
  .refine((a) => a.trigger !== 'followup_missed' || !!a.conditions.hours, { error: 'How many hours late?', path: ['conditions', 'hours'] });
export type AutomationInput = z.input<typeof automationSchema>;

/* Sequences (drip / nurture) */

export const SEQUENCE_ACTION_TYPES = ['whatsapp_template', 'notify', 'set_followup', 'add_tag'] as const;
export const sequenceSchema = z.object({
  name: z.string().trim().min(1, { error: 'Give the sequence a name.' }).max(100),
  isActive: z.boolean().default(true),
  stopOnReply: z.boolean().default(true),
  steps: z
    .array(z.object({
      delayHours: z.coerce.number().min(0).max(24 * 90),
      action: automationActionSchema.refine((a) => (SEQUENCE_ACTION_TYPES as readonly string[]).includes(a.type), { error: 'This action can’t be used in a sequence.' }),
    }))
    .min(1, { error: 'Add at least one step.' })
    .max(20),
});
export type SequenceInput = z.input<typeof sequenceSchema>;

/* Response time (SLA) and lead pool settings */

export const slaSettingsSchema = z.object({
  enabled: z.boolean(),
  firstResponseMinutes: z.coerce.number().int().min(5).max(24 * 60),
  escalateAfterMinutes: z.coerce.number().int().min(10).max(7 * 24 * 60),
});
export type SlaSettings = z.infer<typeof slaSettingsSchema>;
export const DEFAULT_SLA: SlaSettings = { enabled: false, firstResponseMinutes: 30, escalateAfterMinutes: 120 };

export const leadPoolSettingsSchema = z.object({
  enabled: z.boolean(),
  /** How many leads one person may take from the pool per day (0 = no limit). */
  maxClaimsPerDay: z.coerce.number().int().min(0).max(500),
  /** Put a claimed lead back in the pool if its owner doesn't touch it for this many days (0 = never). */
  returnAfterDays: z.coerce.number().int().min(0).max(90),
});
export type LeadPoolSettings = z.infer<typeof leadPoolSettingsSchema>;
export const DEFAULT_LEAD_POOL: LeadPoolSettings = { enabled: false, maxClaimsPerDay: 20, returnAfterDays: 0 };
